// Feature: M004-F0717. Tests: T-M004-0217, T-M004-0218.
//
// Pure model of the TRANSACTION calendar (mode A, design M004-F0712-F0716 section 15): turns the entries of
// listBillingTransactions (Spec core-ledger-account-gateway 1.4.0, TxCalendarEntry) into week lanes and month cells.
//
// ACCT-TX-012: an entry's `window` is a LOCAL WALL-CLOCK time in `window.timezone` ("2026-10-08T20:00"), not an instant.
// This module never converts it to the viewer's zone: it reads the digits and places the entry on the day and minute
// those digits name. (Date objects below are built from the digits only so the shared layout helpers can be reused;
// their zone is irrelevant because nothing here formats an instant.)
import {
  MONTH_NAMES, WEEKDAY_HEADERS, addDays, deriveDayWindow, layoutDayBlocks, localDayKey, minutesOfDay, monthGridRangeOf,
  startOfMonth, weekRangeOf,
} from './bookingsCalendarModel';

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

// "2026-10-08T20:00" -> a Date whose LOCAL fields are exactly those digits; null when it is not a wall-clock string.
export const wallClockToDate = (value) => {
  const match = WALL_CLOCK.exec(String(value || ''));
  if (!match) return null;
  const [, y, m, d, hh, mm] = match.map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};

export const wallClockDayKey = (value) => {
  const match = WALL_CLOCK.exec(String(value || ''));
  return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
};

// The stage families of the month dots (design 15.2).
export const STAGE_FAMILIES = [
  { key: 'attention', label: 'Needs attention', stages: ['dispatch_failed', 'refused', 'returned_for_reprice'] },
  { key: 'waiting', label: 'Waiting', stages: ['captured', 'priced', 'settled', 'awaiting_approval', 'sending_to_ledger', 'handed_off'] },
  { key: 'advice', label: 'In Advice', stages: ['advice'] },
  { key: 'invoice', label: 'On an invoice', stages: ['draft', 'issued'] },
  { key: 'done', label: 'Posted or paid', stages: ['posted', 'paid', 'credited'] },
  { key: 'exit', label: 'Voided or billed elsewhere', stages: ['voided', 'billed_elsewhere'] },
];

export const stageFamilyOf = (stage, condition) => {
  if (condition) return STAGE_FAMILIES[0];
  return STAGE_FAMILIES.find((family) => family.stages.includes(stage)) || STAGE_FAMILIES[1];
};

// One entry -> the occurrence-like record the layout helpers read. `placed` is false for a null / unreadable window.
export const entryToOccurrence = (entry) => {
  const start = wallClockToDate(entry?.window?.start);
  const end = wallClockToDate(entry?.window?.end);
  return {
    key: entry.hand_off_id,
    entry,
    title: entry.title || '',
    start,
    end: end && start && end.getTime() > start.getTime() ? end : start,
    roomId: entry.room ? entry.room.id : null,
    roomName: entry.room ? entry.room.name : '',
    dayKey: wallClockDayKey(entry?.window?.start),
    placed: Boolean(start),
  };
};

// Entries with no readable window never get a grid cell; they are listed under the grid (design 15.6, N15).
export const splitPlacement = (entries) => {
  const placed = [];
  const unplaced = [];
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const occurrence = entryToOccurrence(entry);
    (occurrence.placed ? placed : unplaced).push(occurrence);
  });
  return { placed, unplaced };
};

const NO_ROOM_KEY = 'room:none';

export const buildTxWeekView = ({ occurrences, rooms, anchor }) => {
  const { rangeStart } = weekRangeOf(anchor);
  const days = Array.from({ length: 7 }, (_, index) => addDays(rangeStart, index));
  const dayKeys = new Set(days.map(localDayKey));
  const inWeek = occurrences.filter((occurrence) => dayKeys.has(occurrence.dayKey));

  const lanes = new Map();
  (Array.isArray(rooms) ? rooms : []).forEach((room) => {
    const id = room?.id ?? room?.room_id;
    if (id === undefined || id === null) return;
    lanes.set(`room:${id}`, { key: `room:${id}`, label: room.name || `Room ${id}`, roomId: id });
  });
  inWeek.forEach((occurrence) => {
    const key = occurrence.roomId === null ? NO_ROOM_KEY : `room:${occurrence.roomId}`;
    if (!lanes.has(key)) {
      lanes.set(key, { key, label: occurrence.roomId === null ? 'No room' : occurrence.roomName, roomId: occurrence.roomId });
    }
  });
  const laneList = [...lanes.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));

  const window = deriveDayWindow(inWeek);
  const spanMin = Math.max(1, window.endMin - window.startMin);

  const built = laneList.map((lane) => ({
    ...lane,
    cells: days.map((day) => {
      const dayKey = localDayKey(day);
      const here = inWeek.filter((occurrence) => occurrence.dayKey === dayKey
        && (lane.roomId === null ? occurrence.roomId === null : String(occurrence.roomId) === String(lane.roomId)));
      const blocks = layoutDayBlocks(here.map((occurrence) => ({ occurrence }))).map((block) => {
        const startMin = minutesOfDay(block.occurrence.start);
        const sameDayEnd = block.occurrence.end && localDayKey(block.occurrence.end) === dayKey
          && block.occurrence.end.getTime() > block.occurrence.start.getTime();
        const rawEnd = sameDayEnd ? minutesOfDay(block.occurrence.end) : (block.occurrence.end && block.occurrence.end.getTime() > block.occurrence.start.getTime() ? 24 * 60 : startMin + 30);
        const topPct = ((Math.max(startMin, window.startMin) - window.startMin) / spanMin) * 100;
        const endPct = ((Math.min(rawEnd, window.endMin) - window.startMin) / spanMin) * 100;
        return {
          occurrence: block.occurrence,
          startMin,
          endMin: rawEnd,
          leftPct: block.leftPct,
          widthPct: block.widthPct,
          topPct: Math.max(0, topPct),
          heightPct: Math.min(100 - Math.max(0, topPct), Math.max(6, endPct - topPct)),
        };
      });
      return { day, dayKey, blocks };
    }),
  }));

  return {
    days,
    window,
    lanes: built,
    count: inWeek.length,
    rangeLabel: `${days[0].getDate()} ${MONTH_NAMES[days[0].getMonth()]} – ${days[6].getDate()} ${MONTH_NAMES[days[6].getMonth()]} ${days[6].getFullYear()}`,
  };
};

export const buildTxMonthView = ({ occurrences, anchor }) => {
  const { rangeStart } = monthGridRangeOf(anchor);
  const monthStartDate = startOfMonth(anchor);
  const cells = Array.from({ length: 42 }, (_, index) => {
    const day = addDays(rangeStart, index);
    const dayKey = localDayKey(day);
    const here = occurrences.filter((occurrence) => occurrence.dayKey === dayKey)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    const families = new Map();
    here.forEach((occurrence) => {
      const family = stageFamilyOf(occurrence.entry.stage, occurrence.entry.condition);
      families.set(family.key, { key: family.key, label: family.label, count: (families.get(family.key)?.count || 0) + 1 });
    });
    return {
      day,
      dayKey,
      inMonth: day.getMonth() === monthStartDate.getMonth(),
      count: here.length,
      families: STAGE_FAMILIES.map((family) => families.get(family.key)).filter(Boolean),
      occurrences: here,
    };
  });
  return {
    cells,
    rangeLabel: `${MONTH_NAMES[monthStartDate.getMonth()]} ${monthStartDate.getFullYear()}`,
    weekdayHeaders: WEEKDAY_HEADERS,
  };
};

// "10:00-11:30" read straight from the wall-clock strings of the entry.
export const entryTimeRange = (entry) => {
  const start = WALL_CLOCK.exec(String(entry?.window?.start || ''));
  const end = WALL_CLOCK.exec(String(entry?.window?.end || ''));
  if (!start) return '';
  return end ? `${start[4]}:${start[5]}–${end[4]}:${end[5]}` : `${start[4]}:${start[5]}`;
};

// The RFC3339 `from` / `to` of a visible range, widened by one day each side (design 15.1). Whole UTC instants.
export const rfc3339DayBoundary = (date, offsetDays) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate() + offsetDays, 0, 0, 0));
  return d.toISOString().replace('.000Z', 'Z');
};
